import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useAuth } from "../hooks/useAuth";
import { PermissionDebugger, exposeDebugTools } from "../utils/debugUtils";
import { isSuperAdminUser } from "../utils/superAdmin";
/** 未登录时跳转：优先 msOrg/msAdmin 等在 window 上注入的绝对登录页，并带回跳地址 */
function buildUnifiedLoginHref() {
    if (typeof window === "undefined")
        return "/login";
    const explicit = window
        .REACT_APP_MSLOGIN_LOGIN_URL;
    if (explicit && /^https?:\/\//i.test(explicit.trim())) {
        try {
            const u = new URL(explicit.trim());
            u.searchParams.set("returnUrl", window.location.href);
            return u.toString();
        }
        catch {
            return explicit.trim();
        }
    }
    return "/login";
}
export const AuthGuard = ({ children, requiredPermissions = [], fallback = _jsx("div", { children: "\u52A0\u8F7D\u4E2D..." }), }) => {
    const { isAuthenticated, loading, error, user } = useAuth();
    // 暴露调试工具到浏览器控制台
    React.useEffect(() => {
        exposeDebugTools();
    }, []);
    if (loading) {
        return _jsx(_Fragment, { children: fallback });
    }
    if (error) {
        return (_jsxs("div", { style: { padding: "20px", textAlign: "center" }, children: [_jsx("h3", { children: "\u8BA4\u8BC1\u9519\u8BEF" }), _jsx("p", { children: error }), _jsx("button", { onClick: () => {
                        // 不刷新页面，而是重新进行认证检查
                        // window.location.reload();
                    }, children: "\u91CD\u8BD5" })] }));
    }
    if (!isAuthenticated) {
        return (_jsxs("div", { style: { padding: "20px", textAlign: "center" }, children: [_jsx("h3", { children: "\u9700\u8981\u767B\u5F55" }), _jsx("p", { children: "\u8BF7\u5148\u767B\u5F55\u4EE5\u8BBF\u95EE\u6B64\u9875\u9762" }), _jsx("button", { onClick: () => (window.location.href = buildUnifiedLoginHref()), children: "\u53BB\u767B\u5F55" })] }));
    }
    // 权限检查逻辑
    if (requiredPermissions.length > 0) {
        const hasPermission = checkPermissions(user, requiredPermissions);
        if (!hasPermission) {
            return (_jsxs("div", { style: {
                    padding: "20px",
                    textAlign: "center",
                    background: "#fff2f0",
                    border: "1px solid #ffccc7",
                    borderRadius: "6px",
                    margin: "20px",
                }, children: [_jsx("h3", { style: { color: "#cf1322" }, children: "\u6743\u9650\u4E0D\u8DB3" }), _jsx("p", { children: "\u60A8\u6CA1\u6709\u8BBF\u95EE\u6B64\u9875\u9762\u7684\u6743\u9650" }), _jsxs("p", { style: { fontSize: "12px", color: "#666" }, children: ["\u6240\u9700\u6743\u9650: ", requiredPermissions.join(", ")] }), _jsx("button", { onClick: () => window.history.back(), style: {
                            background: "#1890ff",
                            color: "white",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }, children: "\u8FD4\u56DE" })] }));
        }
    }
    return _jsx(_Fragment, { children: children });
};
/**
 * 检查用户是否有指定权限
 * @param user 用户信息
 * @param requiredPermissions 所需权限列表
 * @returns 是否有权限
 */
function checkPermissions(user, requiredPermissions) {
    const steps = [];
    try {
        steps.push("开始权限检查");
        if (!user) {
            steps.push("❌ 用户不存在");
            PermissionDebugger.log(user, requiredPermissions, false, steps);
            return false;
        }
        steps.push(`✅ 用户存在: ${user.account || user.admin_name || "unknown"}`);
        if (!requiredPermissions || requiredPermissions.length === 0) {
            steps.push("✅ 无需权限验证");
            PermissionDebugger.log(user, requiredPermissions, true, steps);
            return true;
        }
        // 与 useAuthWithConfig 一致：超级管理员不受细粒度 permissions 列表限制
        if (isSuperAdminUser(user)) {
            steps.push("✅ 超级管理员，跳过权限码校验");
            PermissionDebugger.log(user, requiredPermissions, true, steps);
            return true;
        }
        // 获取用户权限列表（只使用permissions，不使用roles）
        const userPermissions = user.permissions || [];
        steps.push(`📋 用户权限列表: [${userPermissions.join(", ")}]`);
        steps.push(`📋 所需权限列表: [${requiredPermissions.join(", ")}]`);
        console.log("[AuthGuard] 统一权限检查:", {
            user: user.account || user.admin_name || "unknown",
            userPermissions: userPermissions,
            requiredPermissions: requiredPermissions,
        });
        // 1. 检查全局通配符权限
        if (userPermissions.includes("*")) {
            steps.push("✅ 用户拥有全局通配符权限");
            console.log("[AuthGuard] ✅ 用户拥有全局通配符权限");
            PermissionDebugger.log(user, requiredPermissions, true, steps);
            return true;
        }
        // 2. 检查是否有任一所需权限
        const hasPermission = requiredPermissions.some((permission) => {
            // 2.1 精确匹配
            if (userPermissions.includes(permission)) {
                steps.push(`✅ 精确匹配权限: ${permission}`);
                console.log(`[AuthGuard] ✅ 精确匹配权限: ${permission}`);
                return true;
            }
            // 2.2 服务级通配符匹配 (例如: msadmin:* 匹配 msadmin:admins:view)
            const servicePrefix = permission.split(":")[0];
            const serviceWildcard = `${servicePrefix}:*`;
            if (userPermissions.includes(serviceWildcard)) {
                steps.push(`✅ 服务通配符匹配: ${serviceWildcard} 匹配 ${permission}`);
                console.log(`[AuthGuard] ✅ 服务通配符匹配: ${serviceWildcard} 匹配 ${permission}`);
                return true;
            }
            // 2.3 功能级通配符匹配 (例如: msadmin:admins:* 匹配 msadmin:admins:view)
            const parts = permission.split(":");
            if (parts.length >= 3) {
                const functionPrefix = parts.slice(0, -1).join(":");
                const functionWildcard = `${functionPrefix}:*`;
                if (userPermissions.includes(functionWildcard)) {
                    steps.push(`✅ 功能通配符匹配: ${functionWildcard} 匹配 ${permission}`);
                    console.log(`[AuthGuard] ✅ 功能通配符匹配: ${functionWildcard} 匹配 ${permission}`);
                    return true;
                }
            }
            steps.push(`❌ 权限不匹配: ${permission}`);
            return false;
        });
        steps.push(`🎯 最终结果: ${hasPermission ? "✅ 通过" : "❌ 拒绝"}`);
        console.log(`[AuthGuard] ${hasPermission ? "✅" : "❌"} 权限检查结果:`, hasPermission);
        PermissionDebugger.log(user, requiredPermissions, hasPermission, steps);
        return hasPermission;
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        steps.push(`❌ 权限检查出错: ${errorMessage}`);
        PermissionDebugger.log(user, requiredPermissions, false, steps);
        return false;
    }
}
